import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { BriefcaseBusiness, Compass, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import PartnerSidebar from "@/components/partners/PartnerSidebar";
import PartnerMobileNav from "@/components/partners/PartnerMobileNav";
import { PartnerProfile } from "@/types/partnerPortal";
import PartnerTour from '@/components/partners/tour/PartnerTour';

const PartnerDashboardLayout = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<PartnerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (!partnerId) {
      navigate("/partner/login", { replace: true });
      return;
    }

    let cancelled = false;
    const loadPortal = async () => {
      try {
        setLoadError(false);
        const sessionRes = await fetch("/api/partner-session.php", { credentials: "include" });
        const session = await sessionRes.json().catch(() => ({}));
        if (!sessionRes.ok || !session.is_partner) {
          navigate("/partner/login", { replace: true });
          return;
        }
        if (String(session.partner_id) !== String(partnerId)) {
          navigate(`/partner/${session.partner_id}/pipeline`, { replace: true });
          return;
        }
        if (session.csrf_token) sessionStorage.setItem("csrf_token", session.csrf_token);

        const profileRes = await fetch("/api/partner-profile.php", {
          credentials: "include",
          headers: { "X-CSRF-Token": session.csrf_token || sessionStorage.getItem("csrf_token") || "" },
        });
        const body = await profileRes.json().catch(() => ({}));
        if (!profileRes.ok || !body.data) throw new Error("Partner profile unavailable");
        if (cancelled) return;

        const data = body.data;
        setPartner({
          id: String(data.id),
          name: data.name || "Συνεργάτης",
          phone: data.phone || "",
          email: data.email || "",
          location: data.location || "",
          services: Array.isArray(data.services) ? data.services : [],
          active: data.active !== false,
          latitude: data.latitude,
          longitude: data.longitude,
          radius: data.radius,
          sms_opt_out: Boolean(data.sms_opt_out),
        });
      } catch (error) {
        console.error("Failed to load partner portal:", error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadPortal();
    return () => { cancelled = true; };
  }, [partnerId, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-sky-600" />
          <p className="mt-3 text-sm font-medium text-slate-600">Φόρτωση του portal…</p>
        </div>
      </div>
    );
  }

  if (!partner || loadError) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 p-5">
        <div className="max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <BriefcaseBusiness className="mx-auto h-8 w-8 text-slate-400" />
          <h1 className="mt-3 text-lg font-bold text-slate-950">Δεν φορτώθηκε το portal</h1>
          <p className="mt-1 text-sm text-slate-600">Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.</p>
          <Button className="mt-5 h-11 w-full" onClick={() => window.location.reload()}>Νέα προσπάθεια</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#f6f8fb] text-slate-950 md:flex">
      <PartnerSidebar partner={partner} />
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 md:hidden">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-500 text-white">
                <BriefcaseBusiness className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">Diathermiki</p>
                <p className="text-[11px] text-slate-500">Portal συνεργατών</p>
              </div>
            </div>
            <div className="hidden md:block">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-400">Συνδεδεμένος συνεργάτης</p>
              <p className="mt-0.5 text-sm font-bold text-slate-900">{partner.name}</p>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Button id="partner-tour-launch" variant="outline" className="h-11 gap-2 rounded-xl border-sky-200 text-sky-800" onClick={() => setTourOpen(true)}><Compass className="h-4 w-4" />Ξενάγηση</Button>
            <div className="hidden min-w-0 items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600 sm:flex">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-sky-600" />
              <span className="max-w-32 truncate sm:max-w-xs">{partner.location || "Χωρίς τοποθεσία"}</span>
            </div>
            </div>
          </div>
        </header>

        <main id="partner-main" className="mx-auto max-w-[1600px] px-4 pb-28 pt-5 sm:px-6 sm:pt-7 md:pb-10 lg:px-8">
          <PartnerTour key={partner.id} partnerId={partner.id} open={tourOpen} onOpenChange={setTourOpen} />
          <Outlet context={{ partner, updatePartner: setPartner }} />
        </main>
      </div>
      <PartnerMobileNav />
    </div>
  );
};

export default PartnerDashboardLayout;
